/*
 * Purpose: AutoBlocks compose session — Mission (running) vs Flow (at rest)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 97
 */
import { create } from 'zustand';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { patchUserState } from '@/lib/user-state-api';
import { WorkflowValidator } from '@/services/validation';
import {
  openConnectorFix,
  openConnectorFixForWorkflow,
  openConnectorFixFromError,
} from '@/stores/connectorFixStore';
import { planReadinessToConnectorIssue } from '@/lib/connectorIssuePolicy';
import { planFromOutcomeAsync } from '@/services/autoblocks/planFromOutcomeAsync';
import { attachWorkflowPreview } from '@/services/autoblocks/workflowAssembler';
import { runDryRun } from '@/services/autoblocks/dryRun';
import { assembleWorkflowFromPlan } from '@/services/autoblocks/workflowAssembler';
import { applyPlanToMap, isMinimalMissionFlow } from '@/lib/applyPlanToMap';
import { expandOutcomeWithDirector, applyDirectorStrategy } from '@/services/autoblocks/director';
import { suggestBrandedBlockIdeas } from '@/lib/brandedBlocks';
import { interpretOutcomeAsync } from '@/services/autoblocks/intentInterpretation';
import type { IntentInterpretation } from '@/services/autoblocks/intentInterpretation';
import {
  resolveInitialComposeMode,
  shouldSwitchToGuided,
  shouldSwitchToExpress,
} from '@/services/autoblocks/flowMode';
import {
  createMissionStep,
  type ArtifactState,
  type ComposeMode,
  type MissionStep,
} from '@/services/autoblocks/missionTypes';
import { auditPlanReadiness, rehydratePlanConnectors } from '@/services/autoblocks/planReadiness';
import type { AutoBlocksPhase, DirectorExpansion, DryRunReport, OutcomePlan } from '@/services/autoblocks/types';
import { useWorkflowStore } from '@/stores/workflowStore';
import { persistWorkflows } from '@/lib/workflowUtils';
import { useExecutionRuntimeStore } from '@/stores/executionRuntimeStore';
import { toast } from 'sonner';
import {
  createMissionSessionId,
  useMissionKnowledgeStore,
} from '@/stores/missionKnowledgeStore';
import { useIntegrationDiscoveryStore } from '@/stores/integrationDiscoveryStore';
import { recordMissionDossier } from '@/services/mission-live/recordLiveAction';
import {
  bindMissionDossierSession,
  missionDossierAiContext,
} from '@/stores/missionDossierStore';
import { resolveKnowledgeRequests } from '@/lib/missionKnowledgeForm';
import {
  allRequiredRiskApprovalsGranted,
  extractRiskItemsFromPlan,
  getPendingRiskApprovals,
} from '@/lib/missionRiskPolicy';
import { retainMissionGeneratedAsync } from '@/services/mission-knowledge/persistGenerated';
import {
  startMissionDirectorEnrichment,
  useMissionDirectorStore,
} from '@/stores/missionDirectorStore';
import { useMissionNotificationStore } from '@/stores/missionNotificationStore';
import { listConnectorCatalogForPrompt } from '@/services/connectors/library/store';

interface AutoBlocksStore {
  phase: AutoBlocksPhase;
  artifactState: ArtifactState;
  composeMode: ComposeMode | null;
  autopilot: boolean;
  isAutopilotRunning: boolean;
  missionTrail: MissionStep[];
  outcomeText: string;
  interpretation: IntentInterpretation | null;
  plan: OutcomePlan | null;
  director: DirectorExpansion | null;
  dryRunReport: DryRunReport | null;
  isInterpreting: boolean;
  isDirectorWorking: boolean;
  isPlanning: boolean;
  isDryRunning: boolean;
  isRunningLive: boolean;
  error: string | null;

  setOutcomeText: (text: string) => void;
  startMission: (text: string, options?: { autopilot?: boolean }) => void;
  interpretOutcome: () => Promise<void>;
  confirmAndPlan: () => Promise<void>;
  expressLaneProceed: () => Promise<void>;
  runAutopilotPipeline: () => Promise<void>;
  setComposeMode: (mode: ComposeMode) => void;
  appendMissionTrail: (step: MissionStep) => void;
  directorExpandAndPlan: () => Promise<void>;
  planOutcome: () => Promise<void>;
  runDryRunTest: () => Promise<void>;
  approveAndOpenInBuilder: () => void;
  approveAndRunLive: () => Promise<void>;
  openPlanInMissionMap: () => void;
  reset: () => void;
}

function pushTrail(
  trail: MissionStep[],
  kind: MissionStep['kind'],
  label: string,
  detail?: string
): MissionStep[] {
  return [...trail, createMissionStep(kind, label, detail)];
}

function ensureMissionKnowledgeSession(): void {
  const store = useMissionKnowledgeStore.getState();
  if (!store.missionSessionId) {
    const id = createMissionSessionId();
    store.setMissionSession(id);
    useIntegrationDiscoveryStore.getState().setMissionSession(id);
    useMissionDirectorStore.getState().setMissionSession(id);
    bindMissionDossierSession(id);
  }
}

function missionKnowledgeContextLocal(getState: () => AutoBlocksStore): string | undefined {
  const { interpretation, outcomeText } = getState();
  const trimmed = outcomeText.trim();
  const requests = interpretation
    ? resolveKnowledgeRequests({
        gaps: interpretation.gaps,
        rawOutcome: interpretation.rawOutcome || trimmed,
        knowledgeRequests: interpretation.knowledgeRequests,
      })
    : [];
  const block = useMissionKnowledgeStore.getState().getSummary(requests).contextBlock.trim();
  return block || undefined;
}

async function missionKnowledgeContextWithRag(getState: () => AutoBlocksStore): Promise<string | undefined> {
  const { interpretation, outcomeText } = getState();
  const trimmed = outcomeText.trim();
  const requests = interpretation
    ? resolveKnowledgeRequests({
        gaps: interpretation.gaps,
        rawOutcome: interpretation.rawOutcome || trimmed,
        knowledgeRequests: interpretation.knowledgeRequests,
      })
    : [];

  const knowledgeStore = useMissionKnowledgeStore.getState();
  void knowledgeStore.syncToServer(trimmed);

  const localBlock = knowledgeStore.getSummary(requests).contextBlock.trim();
  const query =
    interpretation?.interpretedGoal?.trim() ||
    trimmed ||
    'mission outcome requirements scoring resume tailoring';

  const directorRag = useMissionDirectorStore.getState().ragContextBlock.trim();
  const ragBlock =
    directorRag || (await knowledgeStore.fetchRetrievedContext(query, 8));

  const parts: string[] = [];
  if (ragBlock) {
    parts.push(`### Retrieved from mission library (pgvector)\n${ragBlock}`);
  }
  if (localBlock) parts.push(localBlock);

  const merged = parts.join('\n\n').slice(0, 12_000).trim();
  return merged || undefined;
}

function aiMissionContextOptions(missionKnowledgeContext?: string) {
  return {
    missionKnowledgeContext,
    missionDossierContext: missionDossierAiContext(),
  };
}

export const useAutoBlocksStore = create<AutoBlocksStore>((set, get) => ({
  phase: 'describe',
  artifactState: 'mission',
  composeMode: null,
  autopilot: false,
  isAutopilotRunning: false,
  missionTrail: [],
  outcomeText: '',
  interpretation: null,
  plan: null,
  director: null,
  dryRunReport: null,
  isInterpreting: false,
  isDirectorWorking: false,
  isPlanning: false,
  isDryRunning: false,
  isRunningLive: false,
  error: null,

  setOutcomeText: (text) => {
    const { plan, outcomeText: prev } = get();
    const materiallyChanged = plan && text.trim() !== prev.trim();
    if (materiallyChanged) {
      const newSessionId = createMissionSessionId();
      useMissionKnowledgeStore.getState().setMissionSession(newSessionId);
      useIntegrationDiscoveryStore.getState().setMissionSession(newSessionId);
      useMissionDirectorStore.getState().setMissionSession(newSessionId);
      bindMissionDossierSession(newSessionId);
      recordMissionDossier({
        actor: 'human',
        kind: 'outcome_edited',
        summary: 'Outcome edited — new mission session',
        action: text.trim().slice(0, 120),
      });
      set({
        outcomeText: text,
        error: null,
        artifactState: 'mission',
        composeMode: null,
        plan: null,
        director: null,
        dryRunReport: null,
        interpretation: null,
        phase: 'describe',
        missionTrail: pushTrail(get().missionTrail, 'capture', 'Outcome edited — Mission restarted'),
      });
      return;
    }
    set({ outcomeText: text, error: null });
  },

  startMission: (text, options) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const sessionId = createMissionSessionId();
    useMissionKnowledgeStore.getState().setMissionSession(sessionId);
    useIntegrationDiscoveryStore.getState().setMissionSession(sessionId);
    useMissionDirectorStore.getState().setMissionSession(sessionId);
    bindMissionDossierSession(sessionId);
    recordMissionDossier({
      actor: 'human',
      kind: 'mission_started',
      summary: 'Mission started',
      action: trimmed.slice(0, 160),
    });
    set({
      outcomeText: trimmed,
      artifactState: 'mission',
      composeMode: null,
      autopilot: options?.autopilot ?? false,
      isAutopilotRunning: false,
      phase: 'describe',
      interpretation: null,
      plan: null,
      director: null,
      dryRunReport: null,
      error: null,
      missionTrail: [createMissionStep('capture', 'Mission started', trimmed.slice(0, 80))],
    });
  },

  appendMissionTrail: (step) => set({ missionTrail: [...get().missionTrail, step] }),

  setComposeMode: (mode) => {
    const { composeMode: prev, missionTrail } = get();
    if (prev === mode) return;
    set({
      composeMode: mode,
      missionTrail: pushTrail(
        missionTrail,
        'mode_switch',
        `Compose mode → ${mode === 'express' ? 'Express' : 'Guided'}`
      ),
    });
  },

  interpretOutcome: async () => {
    const { outcomeText, missionTrail } = get();
    const trimmed = outcomeText.trim();
    if (!trimmed) return;

    ensureMissionKnowledgeSession();
    const missionSessionId = useMissionKnowledgeStore.getState().missionSessionId;

    set({
      isInterpreting: true,
      error: null,
      phase: 'interpreting',
      artifactState: 'mission',
      composeMode: null,
    });

    recordMissionDossier({
      actor: 'system',
      kind: 'director',
      summary: 'Mission Director — parallel RAG + discovery',
      action: 'Specialists started alongside interpret',
    });

    if (missionSessionId) {
      startMissionDirectorEnrichment({
        missionSessionId,
        outcome: trimmed,
        existingCatalog: listConnectorCatalogForPrompt(),
        phase: 'preamble',
      });
    }

    try {
      const interpretation = await interpretOutcomeAsync(trimmed, {
        ...aiMissionContextOptions(missionKnowledgeContextLocal(get)),
      });
      const mode = resolveInitialComposeMode(interpretation);
      recordMissionDossier({
        actor: 'ai',
        kind: 'interpret',
        summary: `Outcome interpreted (${interpretation.plannerSource})`,
        reasoning: interpretation.interpretedGoal,
        action: `Suggested ${mode} mode · ${Math.round(interpretation.confidence * 100)}% confidence · ${interpretation.gaps.length} gap(s)`,
        payload: {
          assumptions: interpretation.assumptions,
          gaps: interpretation.gaps,
          suggestedComposeMode: mode,
          matchedTemplateHint: interpretation.matchedTemplateHint,
        },
      });
      set({
        interpretation,
        composeMode: mode,
        phase: 'confirm',
        isInterpreting: false,
        missionTrail: pushTrail(missionTrail, 'interpret', 'Outcome interpreted', interpretation.interpretedGoal),
      });

      const missionSessionIdAfter = useMissionKnowledgeStore.getState().missionSessionId;
      if (missionSessionIdAfter) {
        retainMissionGeneratedAsync(missionSessionIdAfter, 'interpret', interpretation, {
          rawOutcome: trimmed,
          structuredAnswers: useMissionKnowledgeStore.getState().structuredAnswers,
        });
        startMissionDirectorEnrichment({
          missionSessionId: missionSessionIdAfter,
          outcome: trimmed,
          interpretedGoal: interpretation.interpretedGoal,
          gaps: interpretation.gaps,
          existingCatalog: listConnectorCatalogForPrompt(),
          phase: 'enrich',
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Interpretation failed';
      recordMissionDossier({
        actor: 'system',
        kind: 'error',
        summary: 'Interpretation failed',
        action: msg,
      });
      set({ error: msg, isInterpreting: false, phase: 'describe' });
    }
  },

  directorExpandAndPlan: async () => {
    const { outcomeText, interpretation, missionTrail } = get();
    const trimmed = outcomeText.trim();
    if (!trimmed) return;

    set({ isDirectorWorking: true, isPlanning: true, error: null, phase: 'director' });

    try {
      await new Promise((r) => setTimeout(r, 400));

      const base = await planFromOutcomeAsync(trimmed, {
        ...aiMissionContextOptions(await missionKnowledgeContextWithRag(get)),
      });
      const director = expandOutcomeWithDirector(
        trimmed,
        interpretation?.interpretedGoal ?? base.interpretedGoal
      );
      const brandedIdeas = suggestBrandedBlockIdeas(trimmed);
      set({ director, phase: 'planning' });

      const strategized = applyDirectorStrategy({
        ...base,
        director,
        brandedIdeas: brandedIdeas.length ? brandedIdeas : undefined,
      });
      const plan = attachWorkflowPreview(rehydratePlanConnectors(strategized));

      const readiness = auditPlanReadiness(plan);
      let composeMode = get().composeMode ?? resolveInitialComposeMode(
        interpretation ?? {
          rawOutcome: trimmed,
          interpretedGoal: base.interpretedGoal,
          confidence: base.confidence,
          suggestedComposeMode: 'guided',
          assumptions: [],
          gaps: [],
          plannerSource: 'rules',
        }
      );

      if (shouldSwitchToGuided(readiness, interpretation) && composeMode === 'express') {
        composeMode = 'guided';
        set({
          composeMode,
          missionTrail: pushTrail(
            get().missionTrail,
            'blocker',
            'Switched to Guided — setup blockers detected'
          ),
        });
      } else if (shouldSwitchToExpress(readiness, interpretation) && composeMode === 'guided') {
        composeMode = 'express';
        set({
          composeMode,
          missionTrail: pushTrail(get().missionTrail, 'mode_switch', 'Readiness green — Express lane')
        });
      }

      set({
        plan,
        director,
        composeMode,
        phase: 'review',
        isDirectorWorking: false,
        isPlanning: false,
        missionTrail: pushTrail(
          missionTrail,
          'plan',
          'Mission map planned',
          `${plan.steps.length} steps · ${director.pacing === 'journey_tube' ? 'Journey Tube' : 'Immediate wiring'}`
        ),
      });

      recordMissionDossier({
        actor: 'ai',
        kind: 'director',
        summary: `Director planned ${plan.steps.length} steps`,
        reasoning: plan.interpretedGoal,
        action: `${director.pacing === 'journey_tube' ? 'Journey Tube pacing' : 'Immediate wiring'} · ${composeMode} mode`,
        payload: {
          plannerSource: plan.plannerSource,
          stepCount: plan.steps.length,
          gaps: plan.gaps,
          assumptions: plan.assumptions,
        },
      });

      recordMissionDossier({
        actor: 'ai',
        kind: 'plan',
        summary: `Outcome plan ready (${plan.plannerSource})`,
        reasoning: base.connectorFactoryNote,
        action: `Connectors: ${plan.connectors?.map((c) => c.id).join(', ') || 'none'}`,
        payload: {
          matchedTemplateId: plan.matchedTemplateId,
          useGmailInbound: plan.useGmailInbound,
        },
      });

      if (base.builtConnectors && base.builtConnectors.length > 0) {
        recordMissionDossier({
          actor: 'ai',
          kind: 'connector_built',
          summary: `Built ${base.builtConnectors.length} connector scaffold(s)`,
          action: base.builtConnectors.map((c) => c.id).join(', '),
          payload: { names: base.builtConnectors.map((c) => c.name) },
        });
      }

      const riskItems = extractRiskItemsFromPlan(plan);
      const needsApproval = riskItems.filter((r) => r.level !== 'safe').length;
      if (needsApproval > 0) {
        recordMissionDossier({
          actor: 'ai',
          kind: 'plan',
          summary: `${needsApproval} item(s) flagged for human approval before use`,
          action: riskItems.map((r) => `${r.summary} (${r.level})`).join('; '),
          payload: { riskItemIds: riskItems.map((r) => r.id) },
        });
      }

      if (director.pacing === 'journey_tube') {
        // Plan update toast via task receipt (MTR)
      }

      const missionSessionId = useMissionKnowledgeStore.getState().missionSessionId;
      if (missionSessionId) {
        const knowledge = useMissionKnowledgeStore.getState();
        const retentionOpts = {
          rawOutcome: trimmed,
          structuredAnswers: knowledge.structuredAnswers,
        };
        retainMissionGeneratedAsync(missionSessionId, 'director', director, retentionOpts);
        retainMissionGeneratedAsync(missionSessionId, 'plan', plan, retentionOpts);
        if (base.builtConnectors?.length) {
          retainMissionGeneratedAsync(
            missionSessionId,
            'connector_factory',
            { builtConnectors: base.builtConnectors, note: base.connectorFactoryNote },
            retentionOpts
          );
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Director planning failed';
      recordMissionDossier({
        actor: 'system',
        kind: 'error',
        summary: 'Director planning failed',
        action: msg,
      });
      set({ error: msg, isDirectorWorking: false, isPlanning: false, phase: 'confirm' });
    }
  },

  confirmAndPlan: async () => {
    const { interpretation } = get();
    if (!interpretation) {
      await get().interpretOutcome();
      if (!get().interpretation) return;
    }
    const mode = get().composeMode ?? resolveInitialComposeMode(get().interpretation!);
    set({
      composeMode: mode,
      phase: mode === 'express' ? 'express_running' : 'planning',
      missionTrail: pushTrail(get().missionTrail, 'confirm', 'Outcome confirmed'),
    });
    recordMissionDossier({
      actor: 'human',
      kind: 'confirm',
      summary: 'Human confirmed outcome',
      action: `${mode} mode selected`,
    });
    if (mode === 'express') {
      await new Promise((r) => setTimeout(r, 300));
    }
    await get().directorExpandAndPlan();
  },

  expressLaneProceed: async () => {
    set({
      composeMode: 'express',
      phase: 'express_running',
      missionTrail: pushTrail(get().missionTrail, 'express', 'Express lane — confirm & plan'),
    });
    await new Promise((r) => setTimeout(r, 300));
    await get().directorExpandAndPlan();
  },

  runAutopilotPipeline: async () => {
    const { outcomeText, missionTrail } = get();
    const trimmed = outcomeText.trim();
    if (!trimmed) return;

    set({
      autopilot: true,
      isAutopilotRunning: true,
      error: null,
      missionTrail: pushTrail(missionTrail, 'express', 'Autopilot — intent to plan'),
    });

    try {
      await get().interpretOutcome();
      if (get().error || !get().interpretation) return;

      const interpretation = get().interpretation!;
      const mode = get().composeMode ?? resolveInitialComposeMode(interpretation);
      set({
        composeMode: mode,
        phase: mode === 'express' ? 'express_running' : 'planning',
        missionTrail: pushTrail(get().missionTrail, 'confirm', 'Autopilot confirmed outcome'),
      });

      if (mode === 'express') {
        await new Promise((r) => setTimeout(r, 300));
      }
      await get().directorExpandAndPlan();
      if (!get().plan || get().error) return;

      await get().runDryRunTest();
      if (get().dryRunReport?.passed) {
        toast.success('Plan ready — unlock vault and connect, then run live');
        if (!AUTH_CONFIG.mock) {
          void patchUserState({
            lastComposeOutcome: trimmed.slice(0, 500),
            lastAutopilotAt: new Date().toISOString(),
            lastPlanId: get().plan?.id ?? null,
          });
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Autopilot failed';
      set({ error: msg, phase: 'describe', autopilot: false });
      toast.error(msg);
    } finally {
      set({ isAutopilotRunning: false });
    }
  },

  planOutcome: async () => {
    const { phase, interpretation } = get();
    if (phase === 'describe' || !interpretation) {
      await get().interpretOutcome();
      return;
    }
    if (phase === 'confirm') {
      await get().confirmAndPlan();
    }
  },

  runDryRunTest: async () => {
    const { plan, missionTrail } = get();
    if (!plan) return;
    set({ isDryRunning: true, error: null, phase: 'dry_run' });
    try {
      const dryRunReport = await runDryRun(plan);
      set({
        dryRunReport,
        isDryRunning: false,
        phase: 'review',
        missionTrail: pushTrail(
          missionTrail,
          'dry_run',
          dryRunReport.passed ? 'Dry run passed' : 'Dry run found issues'
        ),
      });
      recordMissionDossier({
        actor: 'ai',
        kind: 'dry_run',
        summary: dryRunReport.passed ? 'Dry run passed' : 'Dry run found issues',
        action: `${dryRunReport.stepResults?.length ?? 0} steps simulated`,
        payload: {
          passed: dryRunReport.passed,
          readyForReview: dryRunReport.readyForReview,
        },
      });

      const missionSessionId = useMissionKnowledgeStore.getState().missionSessionId;
      if (missionSessionId) {
        retainMissionGeneratedAsync(missionSessionId, 'dry_run', dryRunReport, {
          rawOutcome: plan.rawOutcome,
          structuredAnswers: useMissionKnowledgeStore.getState().structuredAnswers,
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Dry run failed';
      set({ error: msg, isDryRunning: false });
    }
  },

  approveAndOpenInBuilder: () => {
    const { plan, missionTrail } = get();
    if (!plan) return;

    const workflow = assembleWorkflowFromPlan(plan);
    const wfState = useWorkflowStore.getState();
    const workflows = [...wfState.workflows, workflow];
    persistWorkflows(workflows);
    useWorkflowStore.setState({
      workflows,
      currentWorkflow: workflow,
      selectedNode: null,
    });
    set({
      phase: 'approved',
      artifactState: 'flow',
      composeMode: null,
      missionTrail: pushTrail(missionTrail, 'approve', 'Flow validated — at rest'),
    });
    recordMissionDossier({
      actor: 'human',
      kind: 'approve',
      summary: 'Flow validated — saved at rest',
      action: plan.rawOutcome.slice(0, 120),
    });
    toast.success('Your Flow is ready — open Flow Map in Builder');
  },

  approveAndRunLive: async () => {
    const { plan, dryRunReport } = get();
    if (!plan || !dryRunReport?.readyForReview) {
      toast.error('Dry run must pass before a live run', {
        action: { label: 'Retry dry run', onClick: () => void get().runDryRunTest() },
      });
      return;
    }

    const riskItems = extractRiskItemsFromPlan(plan);
    const riskApprovals = useMissionKnowledgeStore.getState().riskApprovals;
    if (!allRequiredRiskApprovalsGranted(riskItems, riskApprovals)) {
      const pending = getPendingRiskApprovals(riskItems, riskApprovals);
      toast.error(
        `${pending.length} risky item${pending.length === 1 ? '' : 's'} need your approval before live run`,
        {
          action: {
            label: 'Review risks',
            onClick: () =>
              document.getElementById('mission-risk-review')?.scrollIntoView({ behavior: 'smooth' }),
          },
        }
      );
      return;
    }

    const readiness = auditPlanReadiness(plan);
    if (!readiness.allRequiredReady && readiness.requiredTotal > 0) {
      const firstMissing = readiness.items.find((i) => i.required && i.status === 'missing');
      if (firstMissing) {
        const missingItems = readiness.items.filter((i) => i.required && i.status === 'missing');
        openConnectorFix({
          issue: planReadinessToConnectorIssue(firstMissing),
          issues: missingItems.map(planReadinessToConnectorIssue),
          onRetry: () => get().runLive(),
        });
      }
      return;
    }

    get().approveAndOpenInBuilder();
    const workflow = useWorkflowStore.getState().currentWorkflow;
    if (!workflow) return;

    const validationErrors = WorkflowValidator.validate(workflow).filter((e) => e.type === 'error');
    if (validationErrors.length > 0) {
      toast.error('Fix flow validation issues before starting', {
        action: { label: 'Open map', onClick: () => { window.location.href = '/mission/map'; } },
      });
      return;
    }

    if (
      openConnectorFixForWorkflow(workflow, {
        onRetry: () => get().runLive(),
      })
    ) {
      return;
    }

    const controller = new AbortController();
    useExecutionRuntimeStore.getState().registerRun(workflow.id, workflow.name, controller);
    set({ isRunningLive: true, error: null });

    try {
      await useWorkflowStore.getState().executeWorkflow(controller.signal);
      if (!controller.signal.aborted) {
        set({
          phase: 'approved',
          artifactState: 'flow',
          missionTrail: pushTrail(get().missionTrail, 'approve', 'Live Mission started'),
        });
        recordMissionDossier({
          actor: 'human',
          kind: 'live_run',
          summary: 'Live mission run started',
          action: workflow.name,
        });
        toast.success(`${workflow.name} is running`);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        toast.message('Run stopped');
        return;
      }
      const msg = error instanceof Error ? error.message : 'Live run failed';
      set({ error: msg });
      if (
        !openConnectorFixFromError(error, {
          workflow,
          onRetry: () => get().runLive(),
        })
      ) {
        toast.error(msg, {
          action: { label: 'Open Mission', onClick: () => { window.location.href = '/mission/map'; } },
        });
      }
    } finally {
      useExecutionRuntimeStore.getState().unregisterRun(workflow.id);
      set({ isRunningLive: false });
    }
  },

  openPlanInMissionMap: () => {
    const { plan } = get();
    if (!plan) return;

    const current = useWorkflowStore.getState().currentWorkflow;
    applyPlanToMap(plan, {
      refine: Boolean(current) && !isMinimalMissionFlow(current),
    });
    useWorkflowStore.getState().saveWorkflow();
  },

  reset: () => {
    const sessionId = createMissionSessionId();
    useMissionKnowledgeStore.getState().setMissionSession(sessionId);
    useIntegrationDiscoveryStore.getState().setMissionSession(sessionId);
    useMissionDirectorStore.getState().setMissionSession(sessionId);
    useMissionDirectorStore.getState().clear();
    bindMissionDossierSession(sessionId);
    useMissionNotificationStore.getState().reset();
    set({
      phase: 'describe',
      artifactState: 'mission',
      composeMode: null,
      autopilot: false,
      isAutopilotRunning: false,
      missionTrail: [],
      outcomeText: '',
      interpretation: null,
      plan: null,
      director: null,
      dryRunReport: null,
      isInterpreting: false,
      isDirectorWorking: false,
      isPlanning: false,
      isDryRunning: false,
      isRunningLive: false,
      error: null,
    });
  },
}));
